import styled, { css } from 'styled-components';
import { ArrowRight } from '../svgs--foldertentative/arrow-right';
import Link from 'next/link';

type colors = 'seedgreen' | 'snowwhite';
type alignment = 'left' | 'center' | 'right';
type transforms = 'upper' | 'lower' | 'capitalize';

export interface TextButtonProps {
  id?: string;
  label?: string;
  url?: string;
  newWindow?: boolean;
  underline?: boolean;
  color?: colors;
  align?: alignment;
  transform?: transforms;
  type?: 'submit' | 'reset' | 'button';
  onClick?: () => void;
  children?: React.ReactNode | string;
  hideArrow?: boolean;
  small?: boolean;
}

const textButtonBase = css<{
  align: alignment;
  transform: transforms;
  underline: boolean;
  small?: boolean;
}>`
  align-items: center;
  display: inline-flex;
  justify-content: ${({ align }) =>
    align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : align};
  gap: 8px;
  border-radius: var(--radius-full);
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--ff, 'Seed Sans');
  font-size: ${({ underline, small }) =>
    underline
      ? small
        ? 'var(--font-size-350)'
        : 'var(--font-size-400)'
      : 'var(--font-size-300)'};
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.04px;
  text-transform: ${({ transform }) =>
    transform === 'upper' ? 'uppercase' : 'capitalize'};
  text-decoration: none;
  position: relative;

  ${({ underline, small }) =>
    underline &&
    `
    font-weight: 500;

    &:after {
      content: '';
      background-color: currentColor;
      position: absolute;
      bottom: -0.3em;
      left: 0;
      height: 1px;
      width: calc(100% - ${small ? '16px' : '20px'});
    }
  `}
`;

const getTextButtonColors = (color: 'seedgreen' | 'snowwhite') => {
  switch (color) {
    case 'seedgreen':
      return {
        textColor: 'var(--fill-brand)',
        arrowColor: 'var(--fill-brand)',
      };
    case 'snowwhite':
      return {
        textColor: 'var(--fill-primary)',
        arrowColor: 'var(--fill-primary)',
      };
    default:
      throw new Error(`Unsupported TextButton color: ${color}`);
  }
};

const StyledButton = styled.button<{
  color: colors;
  align: alignment;
  transform: transforms;
  underline: boolean;
  hideArrow?: boolean;
  small?: boolean;
}>`
  ${textButtonBase}
  ${({ color }) => {
    const { textColor } = getTextButtonColors(color);
    return `
      color: ${textColor};

      &:hover {
        color: ${textColor};
      }
    `;
  }}
  ${({ hideArrow, underline, small }) =>
    hideArrow &&
    `
      height: 48px;
      align-items: center;
      ${
        underline &&
        `
        &:after {
          bottom: calc(50% - 0.5em - 0.3em);
          left: 50%;
          transform: translateX(-50%);
          width: ${small ? '95%' : '100%'};
        }
      `
      }
    `}
`;

const TextButton: React.FC<TextButtonProps> = ({
  id,
  color = 'seedgreen',
  align = 'center',
  label,
  url,
  newWindow,
  type = 'button',
  transform = 'upper',
  underline = false,
  onClick,
  hideArrow,
  small = false,
  children,
}) => {
  const attributes = url
    ? {
        as: Link,
        href: url,
        target: newWindow ? '_blank' : undefined,
        rel: newWindow ? 'noopener noreferrer' : undefined,
      }
    : {
        type: type,
      };

  const arrowDimensions = small ? '8px' : '12px';
  return (
    <StyledButton
      id={id}
      color={color}
      align={align}
      transform={transform}
      underline={underline}
      hideArrow={hideArrow}
      small={small}
      {...attributes}
      onClick={onClick}
    >
      {children ?? label}
      {!hideArrow && (
        <ArrowRight
          color={getTextButtonColors(color).arrowColor}
          style={{ width: arrowDimensions, height: arrowDimensions }}
        />
      )}
    </StyledButton>
  );
};

export default TextButton;
